import type {
  AppointmentRow,
  ServiceHistoryResponse,
} from '../../../stores/useAppointmentsStore';

/**
 * Authoritative wire shape returned by `/reports/consumer/appointments.json`.
 * Source of truth: `/Users/chris/Sera/api/app/datatables/consumer/appointments_datatable.rb#data`.
 *
 * This shape is the flat DataTables row format the legacy Vue 2 portal also
 * consumed (see `/Users/chris/Sera/consumer-portal/src/typings/appointmentReport.ts`).
 * The Vue 3 portal authored `AppointmentRow` against an imagined nested shape;
 * this adapter is the seam where the wire format is reshaped to match.
 */
export interface AppointmentReportRow {
  id: number | string;
  /** "M/D/YYYY" with no leading zeros (e.g., "5/22/2026"). */
  date?: string;
  /** ISO8601 from `Appointment.scheduled_time.iso8601`. */
  datetime?: string;
  service_category_name?: string;
  address_name?: string;
  /** Display-formatted single-line address. */
  address_short?: string;
  invoice_id?: string;
  /** Money string ("$340.00") or sentinel "--" when absent. */
  invoice_total?: string;
  invoice_balance?: string;
  /** Title-cased: "Pending" | "Canceled" | "Completed". */
  status?: string;
}

export interface AppointmentReportResponse {
  data: AppointmentReportRow[];
  recordsFiltered?: number;
}

// Emits "Friday, May 22" (long weekday, short month, no year, UTC). UTC
// keeps formatted weekday/day deterministic across CI timezones. Downstream
// parsers (AppointmentCard, AppointmentsView) match on /^[A-Za-z]+,\s+
// ([A-Za-z]+)\s+(\d{1,2})$/ so either weekday form would parse.
const SHORT_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

/**
 * Parse an "M/D/YYYY" string into a Date in UTC. Returns null on bad input.
 * UTC keeps the formatted weekday/day deterministic across CI timezones.
 */
function parseUsDate(raw: string): Date | null {
  const m = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const month = Number(m[1]);
  const day = Number(m[2]);
  const year = Number(m[3]);
  if (!month || !day || !year) return null;
  const d = new Date(Date.UTC(year, month - 1, day));
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatShortDate(
  iso: string | undefined,
  fallbackDate: string | undefined,
): string {
  // Prefer `fallbackDate` (the wire's tenant-local civil date `M/D/YYYY`) over
  // `iso` (which carries a tenant-tz offset that we then format in UTC). The
  // ISO path used to roll late-evening tenant-tz rows to the next UTC day —
  // e.g. 2026-05-21T20:00:00-04:00 formatted as UTC reads as 2026-05-22.
  // parseUsDate builds via Date.UTC(year, month-1, day) so the formatted
  // weekday/day round-trips cleanly through the UTC formatter.
  if (fallbackDate) {
    const d = parseUsDate(fallbackDate);
    if (d) return SHORT_DATE_FORMATTER.format(d);
  }
  if (iso) {
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) {
      return SHORT_DATE_FORMATTER.format(d);
    }
  }
  return '';
}

function normalizeTotal(raw: string | undefined): string | undefined {
  if (raw === undefined || raw === null) return undefined;
  if (raw === '' || raw === '--') return undefined;
  return raw;
}

// Same shape as normalizeTotal but the AppointmentRow contract uses `null`
// (not undefined) for the invoice currency fields. Keep them aligned so
// `invoice_total === null` and `invoice_balance === null` consistently signal
// "no invoice on this row" to the UI.
function normalizeInvoiceCurrency(raw: string | undefined): string | null {
  if (raw === undefined || raw === null) return null;
  if (raw === '' || raw === '--') return null;
  return raw;
}

function normalizeInvoiceId(raw: string | undefined): number | null {
  if (!raw || raw === '--') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function normalizeId(raw: number | string): number {
  if (typeof raw === 'number') return raw;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function buildAddress(wire: AppointmentReportRow): AppointmentRow['address'] {
  const name = wire.address_name?.trim() ?? '';
  const short = wire.address_short?.trim() ?? '';
  if (!name && !short) return undefined;
  return {
    name: name || undefined,
    // The DataTables payload only ships a single display string; keep callers
    // that read `address1` working by populating it from `address_short`. City
    // and state are unknown at this seam — left blank rather than guessed.
    address1: short,
    city: '',
    state: '',
  };
}

function buildServiceCategory(
  wire: AppointmentReportRow,
): AppointmentRow['service_category'] {
  const name = wire.service_category_name?.trim();
  return name ? { name } : undefined;
}

/**
 * Map one wire row to the nested AppointmentRow shape consumed by the UI.
 *
 * Important guarantees:
 * - `status` is lowercased so existing AppointmentCard display-group switches
 *   (which compare against 'pending', 'completed', 'cancelled', etc.) match.
 *   The wire ships title-cased "Pending"/"Canceled"/"Completed".
 * - `iso_datetime` is passed through verbatim. Callers that need real date
 *   arithmetic (e.g., month grouping across year boundaries) MUST prefer this
 *   field over the yearless `service_date` display string.
 */
export function adaptAppointmentRow(
  wire: AppointmentReportRow,
): AppointmentRow {
  return {
    id: normalizeId(wire.id),
    service_date: formatShortDate(wire.datetime, wire.date),
    status: (wire.status ?? '').toLowerCase(),
    service_category: buildServiceCategory(wire),
    address: buildAddress(wire),
    total: normalizeTotal(wire.invoice_total),
    iso_datetime: wire.datetime,
    invoice_id: normalizeInvoiceId(wire.invoice_id),
    invoice_total: normalizeInvoiceCurrency(wire.invoice_total),
    invoice_balance: normalizeInvoiceCurrency(wire.invoice_balance),
  };
}

/**
 * Map the full DataTables envelope. `recordsFiltered` defaults to `data.length`
 * if the server omits it (shouldn't happen with the Rails datatable, but
 * defensive against test fixtures and future API drift).
 */
export function adaptServiceHistoryResponse(
  wire: AppointmentReportResponse,
): ServiceHistoryResponse {
  const data = (wire.data ?? []).map(adaptAppointmentRow);
  return {
    data,
    recordsFiltered: wire.recordsFiltered ?? data.length,
  };
}
